import React from 'react';
import { Link } from 'react-router-dom';
import { useLegalContent } from '../hooks/useLegalContent';

const DynamicLegalContent: React.FC<{ documentType: 'terms_of_service' | 'privacy_policy' }> = ({ documentType }) => {
  const { content } = useLegalContent(documentType);
  if (!content || !content.sections || content.sections.length === 0) return null;

  return (
    <>
      <div className="mb-12">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
          {documentType === 'terms_of_service' ? 'Terms of Service' : 'Privacy Policy'}
        </h1>
        <p className="text-lg" style={{ color: 'var(--rp-text-secondary)' }}>
          Last Updated: {content.last_updated}
        </p>
      </div>
      <div className="space-y-8">
        {content.sections.map((section) => (
          <div
            key={section.id}
            className="p-6 rounded-lg border"
            style={{ borderColor: 'var(--rp-border)', backgroundColor: 'var(--rp-surface)' }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              {section.title}
            </h2>
            {section.content.split('\n\n').map((paragraph, i) => (
              paragraph.startsWith('\u2022') || paragraph.startsWith('- ') || paragraph.startsWith('* ') ? (
                <ul key={i} className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
                  {paragraph.split('\n').map((item, j) => (
                    <li key={j}>{item.replace(/^[\u2022\-*]\s*/, '')}</li>
                  ))}
                </ul>
              ) : (
                <p key={i} className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
                  {paragraph}
                </p>
              )
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

const TermsOfService: React.FC = () => {
  const { content, loading } = useLegalContent('terms_of_service');
  const hasDbContent = !loading && content && content.sections && content.sections.length > 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rp-background)' }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: 'var(--rp-border)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="text-2xl font-bold" style={{ color: 'var(--rp-primary)' }}>
            RidePulse
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* DB-driven content takes precedence */}
        {hasDbContent ? (
          <DynamicLegalContent documentType="terms_of_service" />
        ) : (
          <>
        {/* Hero Section */}
        <div className="mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
            Terms of Service
          </h1>
          <p className="text-lg" style={{ color: 'var(--rp-text-secondary)' }}>
            Last Updated: October 2, 2026
          </p>
        </div>

        {/* Terms Content */}
        <div className="space-y-8">
          {/* 1. Acceptance of Terms */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              1. Acceptance of Terms
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              Welcome to RidePulse! These Terms of Service ("Terms") govern your use of the RidePulse
              mobile application and related services (collectively, the "Service") operated by Paulo
              Marin, doing business as RidePulse ("RidePulse," "we," "us," or "our"). By accessing or
              using our Service, you agree to be bound by these Terms. If you disagree with any part of
              these Terms, you may not access the Service.
            </p>
          </div>

          {/* 2. Description of Service */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              2. Description of Service
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse is a mobile application that connects motorcycle riders through:
            </p>
            <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>
                <span className="font-semibold">Event Discovery &amp; Organization:</span> Find and
                organize motorcycle rides and events.
              </li>
              <li>
                <span className="font-semibold">Community Building:</span> Connect with fellow riders in
                your area.
              </li>
              <li>
                <span className="font-semibold">Profile Management:</span> Share your riding experience and
                motorcycle information.
              </li>
              <li>
                <span className="font-semibold">Real-Time Communication:</span> Chat with event
                participants and organizers.
              </li>
              <li>
                <span className="font-semibold">Safety Features:</span> Manage emergency contacts and
                safety coordination.
              </li>
              <li>
                <span className="font-semibold">Location Services:</span> Share event locations and use
                navigation assistance.
              </li>
            </ul>
          </div>

          {/* 3. User Accounts and Eligibility */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              3. User Accounts and Eligibility
            </h2>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              3.1 Age Requirement
            </h3>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              You must be at least 16 years old to use RidePulse. Users under 18 should have permission from
              a parent or legal guardian.
            </p>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              3.2 Account Registration
            </h3>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              To access certain features, you must create an account by providing:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Valid email address.</li>
              <li>Profile information (name, experience level, riding style).</li>
              <li>Optional emergency contact information.</li>
              <li>Motorcycle information for event participation.
              </li>
            </ul>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              3.3 Account Responsibilities
            </h3>
            <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Maintain the confidentiality of your account credentials.</li>
              <li>You are responsible for all activity under your account.</li>
              <li>Provide accurate, up-to-date information.</li>
              <li>Notify us immediately of unauthorized use of your account.</li>
            </ul>
          </div>

          {/* 4. Acceptable Use Policy */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              4. Acceptable Use Policy
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              You may use RidePulse to create and join events, connect with other riders, and use our
              communication and safety features. You agree NOT to:
            </p>
            <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Use the Service for any illegal activities.</li>
              <li>Organize events that violate local laws or regulations.</li>
              <li>Harass, threaten, or intimidate other users.</li>
              <li>Share false, misleading, or inappropriate content.</li>
              <li>Attempt to gain unauthorized access to the Service or user accounts.</li>
              <li>Spam or send unsolicited messages.</li>
              <li>Organize or promote dangerous or reckless riding activities.</li>
              <li>Impersonate another person or entity.</li>
              <li>Share others&apos; personal information without their consent.</li>
            </ul>
            <p className="mt-4" style={{ color: 'var(--rp-text-secondary)' }}>
              You acknowledge that motorcycle riding involves inherent risks and that RidePulse does not
              control or supervise actual riding events. You participate in events at your own risk.
            </p>
          </div>

          {/* 5. Content and Intellectual Property */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              5. Content and Intellectual Property
            </h2>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              You retain ownership of the content you create or upload ("User Content"), including profile
              information, event descriptions, messages, and reviews. By sharing User Content, you grant
              RidePulse a non-exclusive, worldwide, royalty-free license to use, display, and distribute your
              content within the Service.
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>User Content must be accurate, lawful, and appropriate.</li>
              <li>You may not infringe the rights of others or violate intellectual property laws.</li>
              <li>
                RidePulse may moderate, remove, or restrict User Content that violates these Terms or legal
                requirements.
              </li>
              <li>
                The Service design, features, and underlying technology are owned by RidePulse and protected
                by intellectual property laws.
              </li>
            </ul>
          </div>

          {/* 6. Event Participation and Liability */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              6. Event Participation and Liability
            </h2>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              Event organizers are responsible for accurate event information, compliance with local laws,
              and the safety of events they host. Participants are responsible for their own safety,
              including:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Reviewing event details carefully before joining.</li>
              <li>Maintaining valid registration, insurance, and safety equipment.</li>
              <li>Following all traffic laws and safety guidelines.</li>
              <li>Informing organizers of relevant health or safety concerns.</li>
            </ul>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse is not responsible for accidents, injuries, property damage, mechanical failures,
              weather conditions, or other issues that occur during events.
            </p>
          </div>

          {/* 7. Privacy and Data Protection */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              7. Privacy and Data Protection
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              Your privacy is important to us. Our collection and use of personal information is governed by
              the RidePulse Privacy Policy, which is incorporated into these Terms by reference. The Privacy
              Policy explains what data we collect, how we use it, and your rights and choices.
            </p>
          </div>

          {/* 8. Location Services */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              8. Location Services
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse may collect location data to show nearby events and riders, provide navigation,
              support emergency contact features, and improve the Service. You can control location sharing
              through device-level permissions and in-app privacy settings.
            </p>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              8.3 GroupPulse Live Tracking
            </h3>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              When you opt in to a GroupPulse session, the Service shares your precise location with
              session participants while the session is active, including when the app is not on screen
              (iOS background location updates; Android foreground service with a persistent notification).
              Sharing ends when you leave the session, the event ends, you sign out, or you revoke location
              permission. On Android, reopening an in-progress event you were sharing in may resume your
              session while permission remains granted.
            </p>
          </div>

          {/* 9. Emergency Features and Contacts */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              9. Emergency Features and Contacts
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              You may provide emergency contact information, which may be shared with event organizers when
              you participate in events and used to notify contacts in case of emergencies. RidePulse&apos;s
              emergency features are supplementary tools and do not replace professional emergency services,
              safety equipment, or training.
            </p>
          </div>

          {/* 10. Payment and Subscriptions */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              10. Payment and Subscriptions
            </h2>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              Basic RidePulse features are provided free of charge. RidePulse Premium is available as a
              monthly subscription for $5.00 USD per month, billed through the store you subscribed on -
              your Apple ID account (App Store) on iOS or your Google Play account on Android.
            </p>
            <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Payment is charged to your store account at confirmation of purchase.</li>
              <li>
                App Store: subscriptions renew automatically unless cancelled at least 24 hours before
                the end of the current period; your account is charged for renewal within 24 hours before
                the period ends. Manage or cancel in your App Store account settings.
              </li>
              <li>
                Google Play: subscriptions renew automatically per Google Play&apos;s billing terms.
                Manage or cancel anytime in Google Play under Payments &amp; subscriptions.
              </li>
              <li>
                All payments, renewals, and refunds are processed by the store where you subscribed under
                that store&apos;s own terms and policies. Nothing in these Terms limits any refund or
                other rights you have under the applicable store&apos;s policies or applicable law.
              </li>
            </ul>
          </div>

          {/* 11. Third-Party Services */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              11. Third-Party Services
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse integrates with third-party services such as Apple services (MapKit, WeatherKit via
              our server, Sign in with Apple, StoreKit, Push Notifications), Google services (Google Maps
              Platform - subject to the Google Maps/Google Earth Additional Terms of Service and the Google
              Privacy Policy - Google Sign-In, Google Play Billing, Firebase Cloud Messaging), Supabase
              (database and backend infrastructure), and Resend (email delivery). Events may include
              optional external links provided by the organizer (for example, a Facebook event page). Your
              use of these third-party services is subject to their respective terms and privacy policies.
              We do not control and are not responsible for their practices.
            </p>
          </div>

          {/* 12. Suspension and Termination */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              12. Suspension and Termination
            </h2>
            <p className="mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
              You may terminate your account at any time by deleting it in the app, submitting an
              &quot;Account or data deletion&quot; request at ridepulse.app/contact, or contacting
              support. We may suspend or terminate your account if you:
            </p>
            <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--rp-text-secondary)' }}>
              <li>Violate these Terms or our community guidelines.</li>
              <li>Engage in illegal activities or create safety risks.</li>
              <li>Attempt to circumvent our technical measures.</li>
              <li>Fail to pay for premium services when required.</li>
            </ul>
          </div>

          {/* 13. Disclaimers and Warranties */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              13. Disclaimers and Warranties
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse is provided "as is" and "as available" without warranties of any kind, whether
              express or implied. We do not guarantee uninterrupted service, error-free operation, compatibility
              with all devices, or the accuracy of user-generated content or third-party information.
            </p>
          </div>

          {/* 14. Limitation of Liability */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              14. Limitation of Liability
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              To the maximum extent permitted by law, RidePulse shall not be liable for indirect, incidental,
              special, consequential, or punitive damages, including lost profits, data, or business
              opportunities, or for personal injury, property damage, third-party actions, or technical issues
              arising from use of the Service.
            </p>
          </div>

          {/* 15. Indemnification */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              15. Indemnification
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              You agree to indemnify and hold harmless RidePulse, its affiliates, officers, directors,
              employees, and agents from any claims, damages, losses, or expenses arising from your use of the
              Service, your participation in events, your violation of these Terms, your infringement of third-
              party rights, or your User Content.
            </p>
          </div>

          {/* 16. Modifications to Terms */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              16. Modifications to Terms
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              RidePulse may modify these Terms at any time by posting updated Terms in the app or Service and,
              where appropriate, providing additional notice. Continued use of the Service after changes are
              effective constitutes acceptance of the updated Terms.
            </p>
          </div>

          {/* 17. Governing Law and Disputes */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              17. Governing Law and Disputes
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              These Terms are governed by the laws of the State of California and the United States of America
              without regard to conflict of law principles. Disputes may be addressed through informal
              resolution, mediation, arbitration, or courts as described in the full Terms. You agree not to
              participate in class or representative actions against RidePulse.
            </p>
          </div>

          {/* 18. Miscellaneous */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              18. Miscellaneous
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              These Terms, together with the Privacy Policy, constitute the entire agreement between you and
              RidePulse regarding the Service. If any provision is found unenforceable, the remaining
              provisions remain in full force. Our failure to enforce any right is not a waiver. RidePulse is
              not liable for delays or failures due to causes beyond our reasonable control.
            </p>
          </div>

          {/* 19. Contact Information */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              19. Contact Information
            </h2>
            <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="mb-2 font-medium" style={{ color: 'var(--rp-primary)' }}>
              support@ridepulse.app
            </p>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              Additional support information is available at ridepulse.app/contact.
            </p>
          </div>

          {/* 20. Acknowledgment */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              20. Acknowledgment
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              BY USING RIDEPULSE, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THESE TERMS, AGREE TO BE
              BOUND BY THEM, UNDERSTAND THE RISKS ASSOCIATED WITH MOTORCYCLE RIDING, AND ACCEPT RESPONSIBILITY
              FOR YOUR OWN SAFETY AND COMPLIANCE WITH APPLICABLE LAWS.
            </p>
          </div>

          {/* 21. Additional Provisions */}
          <div
            className="p-6 rounded-lg border"
            style={{
              borderColor: 'var(--rp-border)',
              backgroundColor: 'var(--rp-surface)',
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
              21. Additional Provisions
            </h2>
            <p style={{ color: 'var(--rp-text-secondary)' }}>
              Additional details about chat message retention, content moderation timelines, data export
              requests, California privacy rights, and our initial launch scope (United States and Canada)
              are described in more detail in our Privacy Policy and product documentation. These provisions
              are incorporated by reference and help clarify how RidePulse operates as the product evolves.
            </p>
          </div>
        </div>
        </>)}

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t" style={{ borderColor: 'var(--rp-border)' }}>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link 
              to="/" 
              className="hover:underline"
              style={{ color: 'var(--rp-text-secondary)' }}
            >
              Home
            </Link>
            <Link 
              to="/legal/privacy" 
              className="hover:underline"
              style={{ color: 'var(--rp-text-secondary)' }}
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
